/**
 * Number and time formatting for the DOM overlay.
 *
 * Kept pure and DOM-free so it can be unit-tested in Node, and kept in one
 * place because the HUD refreshes at the simulation's 10Hz: inconsistent
 * rounding between two panels showing the same number reads as a bug, and a
 * value whose digit count changes every tick makes the whole bar twitch.
 *
 * Nothing here decides anything about the game. It turns numbers the server
 * already produced into strings.
 */

/** Placeholder for a value that isn't a finite number, rather than "NaN". */
const NO_VALUE = "—";

/**
 * Remaining session time as `mm:ss`.
 *
 * Rounds **up**, so the final partial second reads `00:01` and `00:00` means
 * the session is actually over — a clock that shows `00:00` for a whole second
 * before anything happens reads as a hung game.
 */
export function formatClock(ticksRemaining: number, tickRateHz: number): string {
  if (!Number.isFinite(ticksRemaining) || !Number.isFinite(tickRateHz) || tickRateHz <= 0) {
    return "--:--";
  }

  const totalSeconds = Math.max(0, Math.ceil(ticksRemaining / tickRateHz));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  return `${pad2(minutes)}:${pad2(seconds)}`;
}

/**
 * A resource amount, compact enough to stay a fixed width in a tabular column.
 *
 * Below 10 keeps one decimal, because the difference between 0 and 0.4 food
 * matters when you are starving. Above it, decimals are noise that jitters ten
 * times a second, so they go.
 */
export function formatAmount(n: number): string {
  if (!Number.isFinite(n)) return NO_VALUE;

  const magnitude = Math.abs(n);
  if (magnitude >= 1_000_000) return `${trim(n / 1_000_000, 1)}M`;
  if (magnitude >= 10_000) return `${trim(n / 1000, 1)}k`;
  if (magnitude >= 10) return `${Math.round(n)}`;
  return trim(n, 1);
}

/**
 * A signed per-second rate, e.g. `+2.4/s`, `-0.6/s`, `0.0/s`.
 *
 * The sign is always shown for non-zero values: on a resource bar, "is this
 * number climbing or draining" is the thing the player is actually reading, and
 * an unsigned `2.4/s` next to an unsigned `0.6/s` hides it.
 */
export function formatRate(perSecond: number): string {
  if (!Number.isFinite(perSecond)) return `${NO_VALUE}/s`;

  const rounded = Math.round(perSecond * 10) / 10;
  if (rounded === 0) return "0.0/s";

  const magnitude = Math.abs(rounded);
  const digits = magnitude >= 100 ? formatAmount(magnitude) : magnitude.toFixed(1);
  return `${rounded > 0 ? "+" : "-"}${digits}/s`;
}

/** A 0..1 fraction as a whole percentage, e.g. `65%`. */
export function formatPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return NO_VALUE;
  return `${Math.round(fraction * 100)}%`;
}

/** `0xRRGGBB` as a CSS colour. The wire format for kingdom colour is a number. */
export function formatColor(rgb: number): string {
  const clamped = Number.isFinite(rgb) ? Math.max(0, Math.min(0xffffff, Math.round(rgb))) : 0;
  return `#${clamped.toString(16).padStart(6, "0")}`;
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

/** Fixed-decimal, with a trailing `.0` dropped so whole numbers stay short. */
function trim(value: number, decimals: number): string {
  const text = value.toFixed(decimals);
  // `-0.0` is arithmetically correct and visually wrong.
  const normalised = text === `-${(0).toFixed(decimals)}` ? (0).toFixed(decimals) : text;
  return normalised.endsWith(".0") ? normalised.slice(0, -2) : normalised;
}
